/**
 * UsersPage (/compte/utilisateurs) — the capability-gated management surface (cap:
 * iam.utilisateur.manage). Consumes the REAL endpoint GET /utilisateurs, which today returns a
 * 501 NOT_IMPLEMENTED stub (server/src/modules/iam: errors.notImplemented('iam.utilisateur.list')).
 * The screen treats 501 as a first-class "surface not yet available" EMPTY state rather than an
 * error — so the day the iam list lands, this view renders rows with zero code change.
 *
 * 5-state DoD: ProtectedRoute handles sans-droit (module requiredCapabilities); useCan double-gates
 * the nav entry; OfflineState reads useUiStore; the Table wires loading/error/empty; the 501 is
 * mapped to empty.
 */
import { useMemo } from "react";
import { Card, ForbiddenState, OfflineState, Table, type Column } from "@/ui";
import { useApiQuery, qk, ERROR_CODES } from "@/api";
import { useT, useUiStore } from "@/app";
import { useAuthStore, useCan } from "@/auth";
import { AUTH_CAPS } from "./messages";

interface UserRow {
  id: string;
  nomComplet: string;
  email: string;
  preset?: string;
}

export function UsersPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const labId = useAuthStore((s) => s.lab?.id ?? s.user?.laboratoireId ?? null);
  const canManage = useCan(AUTH_CAPS.usersManage);

  const { data, isLoading, isError, error, refetch } = useApiQuery<UserRow[]>(
    qk.scoped(labId, "iam", "utilisateurs"),
    "/utilisateurs",
    { enabled: canManage },
  );

  // 501 (NOT_IMPLEMENTED) → treat as "surface not yet available" (empty), not a hard error.
  const notImplemented =
    isError && error?.httpStatus === 501 && error?.code === ERROR_CODES.NOT_IMPLEMENTED;

  const columns: ReadonlyArray<Column<UserRow>> = useMemo(
    () => [
      {
        key: "nom",
        header: t("auth.users.col.name"),
        sortable: true,
        sortAccessor: (r) => r.nomComplet,
        cell: (r) => r.nomComplet,
      },
      {
        key: "email",
        header: t("auth.users.col.email"),
        mono: true,
        sortable: true,
        sortAccessor: (r) => r.email,
        cell: (r) => <span className="nv-ltr-island">{r.email}</span>,
      },
      {
        key: "role",
        header: t("auth.users.col.role"),
        cell: (r) => r.preset ?? "—",
      },
    ],
    [t],
  );

  // Belt-and-braces sans-droit (ProtectedRoute already gates the module; this guards a direct hit).
  if (!canManage) {
    return <ForbiddenState requiredCapabilities={[AUTH_CAPS.usersManage]} />;
  }
  if (!online) {
    return <OfflineState onRetry={() => void refetch()} cached={!!data} />;
  }

  const tableError =
    isError && !notImplemented
      ? { message: error?.message ?? t("state.offline.title"), onRetry: () => void refetch() }
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
        <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("auth.users.title")}</h1>
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("auth.users.subtitle")}</p>
      </header>

      <Card padded={false}>
        <Table<UserRow>
          columns={columns}
          rows={notImplemented ? [] : (data ?? [])}
          rowKey={(r) => r.id}
          caption={t("auth.users.title")}
          loading={isLoading}
          error={tableError}
          emptyTitle={notImplemented ? t("auth.users.empty.title") : t("auth.users.col.name")}
          {...(notImplemented ? { emptyDescription: t("auth.users.empty.desc") } : {})}
          pageSize={20}
        />
      </Card>
    </div>
  );
}
